import React, { useEffect, useMemo, useState } from 'react';
import { Container, TextField, Box, Button, Typography, MenuItem, FormControlLabel, Checkbox, Collapse, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, CircularProgress } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import ErrorDialog from '../../components/global/ErrorDialog';
import ImageSelect from '../../components/global/images/ImageSelector';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import SuccessOrErrorDialog from '../../components/global/SuccessOrErrorDialog';
import { DAYS_OF_WEEK_PREFERRED_ORDER, TIMEZONES } from '../../constants';
import { getBusinessConfigById, getEmployeeWorkHours, rebuildStatsForSite, rebuildCustomersForSite, updateBusinessConfig } from '../../firebase/firebase-delegate';
import { BusinessConfig, createEmptyBusinessConfig } from '../../types';
import { useNavigate, useParams } from 'react-router-dom';
import {
    convertHoursToEntries,
    entriesToHoursMap,
    BusinessHoursEntry,
    DayKey,
    cloneBusinessHoursEntries,
    convertWorkHoursToBusinessEntries,
} from '../../utils/businessHours';
import BusinessHoursCard from '../../components/admin/BusinessHoursCard';

const EditBusinessConfigPage: React.FC = () => {
    const [updatedBusinessConfig, setUpdatedBusinessConfig] = useState<BusinessConfig | null>(createEmptyBusinessConfig());
    const [businessHoursEntries, setBusinessHoursEntries] = useState<Record<DayKey, BusinessHoursEntry>>(
        () => convertHoursToEntries()
    );

    const [formLoading, setFormLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [heroImageChangePlaceHolder, setHeroImageChangePlaceHolder] = useState<File | undefined>(undefined);
    const [logoImageChangePlaceHolder, setLogoImageChangePlaceHolder] = useState<File | undefined>(undefined);
    const [heroLogoImageChangePlaceHolder, setHeroLogoImageChangePlaceHolder] = useState<File | undefined>(undefined);
    const [businessHoursOpen, setBusinessHoursOpen] = useState(false);

    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [resultDialogOpen, setResultDialogOpen] = useState(false);
    const [rebuildDialogOpen, setRebuildDialogOpen] = useState(false);
    const [rebuildCustomersDialogOpen, setRebuildCustomersDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const { siteId } = useParams();
    const navigate = useNavigate();

    const businessConfigQuery = useQuery<BusinessConfig | null>({
        queryKey: ['businessConfig', siteId],
        enabled: !!siteId,
        queryFn: () => getBusinessConfigById(siteId!)
    });

    const ownerId = updatedBusinessConfig?.paymentInfo?.owner;

    const ownerWorkHoursQuery = useQuery({
        queryKey: ['siteOwnerWorkHours', ownerId, siteId],
        queryFn: () => getEmployeeWorkHours(ownerId!, siteId!),
        enabled: !!ownerId && !!siteId,
    });

    const ownerWorkHoursEntries = useMemo(
        () => (ownerWorkHoursQuery.data ? convertWorkHoursToBusinessEntries(ownerWorkHoursQuery.data) : null),
        [ownerWorkHoursQuery.data]
    );

    const updateBusinessConfigMutation = useMutation({
        mutationFn: async () => {
            if (!updatedBusinessConfig || !siteId) {
                throw new Error("buisness data is missing");
            }
            if (updatedBusinessConfig.homepage.heroImagePath === "" && !heroImageChangePlaceHolder) {
                throw new Error("Hero image is missing");
            }
            const trimmedWebsiteDomain = updatedBusinessConfig.businessInfo.websiteDomain?.trim() ?? '';
            const normalizedHours = entriesToHoursMap(businessHoursEntries);
            const sanitizedConfig: BusinessConfig = {
                // Persist a trimmed domain without mutating local state, so we don't save accidental whitespace.
                ...updatedBusinessConfig,
                businessInfo: {
                    ...updatedBusinessConfig.businessInfo,
                    websiteDomain: trimmedWebsiteDomain,
                    businessHours: normalizedHours,
                },
            };
            if (sanitizedConfig.preferences?.orchestratorMode !== true) {
                sanitizedConfig.preferences = {
                    ...(sanitizedConfig.preferences ?? {}),
                    bookingModel: 'staff',
                };
            }
            await updateBusinessConfig(
                sanitizedConfig,
                heroImageChangePlaceHolder,
                logoImageChangePlaceHolder,
                heroLogoImageChangePlaceHolder,
                siteId
            );
        },
        onSuccess: () => {
            console.log("Business config updated successfully");
        },
        onError: (error: Error) => {
            setErrorMessage(error.message);
            console.error("Error updating employee: ", error);
        },
        onSettled: () => {
            setResultDialogOpen(true);
        }
    });

    const rebuildStatsMutation = useMutation({
        mutationFn: async () => {
            if (!siteId) throw new Error("Site ID is missing");
            await rebuildStatsForSite(siteId);
        },
        onSuccess: () => {
            setSnackbar({ open: true, message: 'Analytics stats rebuilt successfully.', severity: 'success' });
        },
        onError: (error: Error) => {
            setSnackbar({ open: true, message: error.message || 'Failed to rebuild analytics stats.', severity: 'error' });
        },
        onSettled: () => setRebuildDialogOpen(false),
    });

    const rebuildCustomersMutation = useMutation({
        mutationFn: async () => {
            if (!siteId) throw new Error("Site ID is missing");
            await rebuildCustomersForSite(siteId);
        },
        onSuccess: () => {
            setSnackbar({ open: true, message: 'Customer data rebuilt successfully.', severity: 'success' });
        },
        onError: (error: Error) => {
            setSnackbar({ open: true, message: error.message || 'Failed to rebuild customer data.', severity: 'error' });
        },
        onSettled: () => setRebuildCustomersDialogOpen(false),
    });

    const isLoading = businessConfigQuery.isLoading || businessConfigQuery.isFetching;
    const isError = businessConfigQuery.isError;

    useEffect(() => {
        if (businessConfigQuery.data) {
            const normalizedHours = entriesToHoursMap(convertHoursToEntries(businessConfigQuery.data.businessInfo?.businessHours));
            const next: BusinessConfig = {
                ...businessConfigQuery.data,
                businessInfo: {
                    ...businessConfigQuery.data.businessInfo,
                    businessHours: normalizedHours,
                },
                homepage: {
                    ...businessConfigQuery.data.homepage,
                    // Older configs may not have logoPlacement set yet; default to navbar for a predictable UI.
                    // TODO Delete this fallback after a few months 
                    // This was added on 10-14-25!
                    logoPlacement: businessConfigQuery.data.homepage.logoPlacement ?? 'navbar',
                    heroLogoImagePath: businessConfigQuery.data.homepage.heroLogoImagePath ?? '',
                },
                features: {
                    ...businessConfigQuery.data.features,
                    enableCart: businessConfigQuery.data.features?.enableCart ?? false,
                    loginlessEmployeeCount:
                        businessConfigQuery.data.features?.loginlessEmployeeCount
                        ?? businessConfigQuery.data.features?.employeeCount
                        ?? 1,
                },
                // Ensure paymentInfo exists so schema-based fields (like skipSubscriptionCheck) render
                paymentInfo: businessConfigQuery.data.paymentInfo ?? {
                    owner: '',
                    skipSubscriptionCheck: false,
                },
                payoutInfo: businessConfigQuery.data.payoutInfo ?? {
                    useBusinessStripeAccount: false,
                    businessStripeAccountId: "",
                },
                preferences: {
                    ...(businessConfigQuery.data.preferences ?? {}),
                    uiFlags: {
                        showHomepageTeamSection: businessConfigQuery.data.preferences?.uiFlags?.showHomepageTeamSection ?? true,
                        showHomepageReviewsSection: businessConfigQuery.data.preferences?.uiFlags?.showHomepageReviewsSection ?? true,
                        showHomepageGallerySection: businessConfigQuery.data.preferences?.uiFlags?.showHomepageGallerySection ?? true,
                        showHomepageAboutSection: businessConfigQuery.data.preferences?.uiFlags?.showHomepageAboutSection ?? true,
                        showEmployeeBio: businessConfigQuery.data.preferences?.uiFlags?.showEmployeeBio ?? true,
                        showEmployeeSocials: businessConfigQuery.data.preferences?.uiFlags?.showEmployeeSocials ?? true,
                        showEmployeeProfilePhoto: businessConfigQuery.data.preferences?.uiFlags?.showEmployeeProfilePhoto ?? true,
                        showEmployeePickerInBooking: businessConfigQuery.data.preferences?.uiFlags?.showEmployeePickerInBooking ?? true,
                    },
                    serviceCategories: businessConfigQuery.data.preferences?.serviceCategories ?? [],
                    requirePaymentForBooking: businessConfigQuery.data.preferences?.requirePaymentForBooking ?? false,
                    allowTips: businessConfigQuery.data.preferences?.allowTips ?? false,
                    minimumBookingNoticeHours: businessConfigQuery.data.preferences?.minimumBookingNoticeHours ?? 1,
                    enableEmployeeEmails:
                        businessConfigQuery.data.preferences?.enableEmployeeEmails ??
                        false,
                    allowAnyAvailableEmployee:
                        businessConfigQuery.data.preferences?.allowAnyAvailableEmployee ??
                        false,
                    bookingModel:
                        businessConfigQuery.data.preferences?.bookingModel ??
                        'staff',
                    terminology: {
                        resourceSingular:
                            businessConfigQuery.data.preferences?.terminology?.resourceSingular ?? 'Employee',
                        resourcePlural:
                            businessConfigQuery.data.preferences?.terminology?.resourcePlural ?? 'Employees',
                        anyAvailableLabel:
                            businessConfigQuery.data.preferences?.terminology?.anyAvailableLabel ?? 'Any Available',
                        selectResourceLabel:
                            businessConfigQuery.data.preferences?.terminology?.selectResourceLabel ?? 'Select employee',
                    },
                    orchestratorMode:
                        businessConfigQuery.data.preferences?.orchestratorMode ??
                        false,
                    primaryOrchestratorId:
                        businessConfigQuery.data.preferences?.primaryOrchestratorId ??
                        '',
                },
            };
            setUpdatedBusinessConfig(next);
            setBusinessHoursEntries(convertHoursToEntries(normalizedHours));
        }
    }, [businessConfigQuery.data]);

    const handleChange = (
        section: keyof BusinessConfig,
        field: string,
        value: any
    ) => {
        setUpdatedBusinessConfig((prevData) => {
            if (!prevData) {
                return null;
            }

            const next: BusinessConfig = {
                ...prevData,
                [section]: {
                    ...prevData[section as keyof typeof prevData],
                    [field]: value,
                } as any,
            };

            if (section === 'preferences' && field === 'orchestratorMode' && value === true) {
                next.payoutInfo = {
                    ...(next.payoutInfo ?? { useBusinessStripeAccount: false }),
                    useBusinessStripeAccount: true,
                };
                const ownerEmployeeId = next.paymentInfo?.owner?.trim() ?? '';
                if (!next.preferences?.primaryOrchestratorId?.trim() && ownerEmployeeId) {
                    next.preferences = {
                        ...(next.preferences ?? {}),
                        primaryOrchestratorId: ownerEmployeeId,
                    };
                }
            }

            if (section === 'preferences' && field === 'orchestratorMode' && value === false) {
                next.preferences = {
                    ...(next.preferences ?? {}),
                    bookingModel: 'staff',
                    uiFlags: {
                        ...((next.preferences ?? {}).uiFlags ?? {}),
                        showEmployeePickerInBooking: true,
                    },
                    terminology: {
                        ...((next.preferences ?? {}).terminology ?? {}),
                        resourceSingular: 'Employee',
                        resourcePlural: 'Employees',
                        anyAvailableLabel: 'Any Available',
                        selectResourceLabel: 'Select employee',
                    },
                };
            }

            if (section === 'features' && field === 'enablePayments' && value === false) {
                next.preferences = {
                    ...(next.preferences ?? {}),
                    requirePaymentForBooking: false,
                    allowTips: false,
                };
            }

            if (
                section === 'payoutInfo' &&
                field === 'useBusinessStripeAccount' &&
                prevData.preferences?.orchestratorMode === true &&
                value === false
            ) {
                next.payoutInfo = {
                    ...(next.payoutInfo ?? { useBusinessStripeAccount: false }),
                    useBusinessStripeAccount: true,
                };
            }

            if (section === 'paymentInfo' && field === 'skipSubscriptionCheck' && value === false) {
                next.paymentInfo = {
                    ...(next.paymentInfo ?? { owner: '', skipSubscriptionCheck: false }),
                    currentPeriodEnd: Date.now(),
                };
            }

            return next;
        });
    };

    const handleBusinessHoursEntryChange = (day: DayKey, nextEntry: BusinessHoursEntry) => {
        setBusinessHoursEntries((prevEntries) => ({
            ...prevEntries,
            [day]: nextEntry,
        }));
    };

    const handlePreferenceNestedChange = (
        group: 'uiFlags' | 'terminology',
        key: string,
        value: any
    ) => {
        setUpdatedBusinessConfig((prevData) => {
            if (!prevData) {
                return null;
            }

            return {
                ...prevData,
                preferences: {
                    ...(prevData.preferences ?? {}),
                    [group]: {
                        ...((prevData.preferences as any)?.[group] ?? {}),
                        [key]: value,
                    },
                },
            };
        });
    };

    const handleBusinessHoursPreset = (preset: 'default' | 'clear') => {
        if (!updatedBusinessConfig) return;
        if (preset === 'default') {
            const presetHours = {
                monday: '9:00AM - 5:00PM',
                tuesday: '9:00AM - 5:00PM',
                wednesday: '9:00AM - 5:00PM',
                thursday: '9:00AM - 5:00PM',
                friday: '9:00AM - 5:00PM',
                saturday: 'CLOSED',
                sunday: 'CLOSED',
            } as BusinessConfig['businessInfo']['businessHours'];
            setBusinessHoursEntries(convertHoursToEntries(presetHours));
            return;
        }

        const cleared = {
            monday: '',
            tuesday: '',
            wednesday: '',
            thursday: '',
            friday: '',
            saturday: '',
            sunday: '',
        } as BusinessConfig['businessInfo']['businessHours'];
        setBusinessHoursEntries(convertHoursToEntries(cleared));
    };

    const handleMatchBusinessHours = () => {
        if (!ownerWorkHoursEntries) return;
        setBusinessHoursEntries(cloneBusinessHoursEntries(ownerWorkHoursEntries));
    };

    const matchToggleDisabled = !ownerWorkHoursEntries || ownerWorkHoursQuery.isLoading;
    const orderedBusinessDays = DAYS_OF_WEEK_PREFERRED_ORDER as ReadonlyArray<DayKey>;

    const onHeroImageChange = (imageFile?: File) => {
        setHeroImageChangePlaceHolder(imageFile);
    };

    const onLogoImageChange = (imageFile?: File) => {
        setLogoImageChangePlaceHolder(imageFile);
    };

    const onHeroLogoImageChange = (imageFile?: File) => {
        setHeroLogoImageChangePlaceHolder(imageFile);
    };

    // Schema-driven form config
    type FieldType = 'text' | 'email' | 'phone' | 'url' | 'textarea' | 'number' | 'boolean' | 'select' | 'readonly' | 'hours';
    type FieldSpec = { type: FieldType; label?: string; options?: ReadonlyArray<string>; required?: boolean; readOnly?: boolean };
    const commonSx = { width: '80%', mb: 2 } as const;

    const schema: Record<string, Record<string, FieldSpec>> = {
        businessInfo: {
            name: { type: 'text', required: true },
            address: { type: 'text', required: true },
            phone: { type: 'phone', required: true },
            email: { type: 'email', required: false },
            websiteDomain: { type: 'text', label: 'Website Domain' },
            timezone: { type: 'select', options: TIMEZONES, required: true },
            businessHours: { type: 'hours', label: 'Business Hours' },
        },
        socials: {
            facebook: { type: 'url' },
            instagram: { type: 'url' },
            twitter: { type: 'url' },
        },
        homepage: {
            heroTitle: { type: 'text', required: true },
            heroSubtitle: { type: 'text', required: true },
            aboutUs: { type: 'textarea' },
            googleMapsSrc: { type: 'url', required: true },
            logoPlacement: { type: 'select', label: 'Logo Placement', options: ['navbar', 'heroOverlay', 'both'], required: true },
        },
        features: {
            enableCustomerSMS: { type: 'boolean', label: 'Enable Customer SMS' },
            enableEmployeeSMS: { type: 'boolean', label: 'Enable Employee SMS' },
            enablePayments: { type: 'boolean', label: 'Enable Payments' },
            enableCart: { type: 'boolean', label: 'Enable Cart' },
            employeeCount: { type: 'number', label: 'Employee Count' },
        },
        paymentInfo: {
            planType: { type: 'readonly', label: 'Plan Type' },
            skipSubscriptionCheck: { type: 'boolean', label: 'Skip Subscription Requirement' },
            stripeCustomerId: { type: 'readonly' },
            stripeSubscriptionId: { type: 'readonly' },
            subscriptionStatus: { type: 'readonly' },
            currentPeriodEnd: { type: 'readonly', label: 'Current Period End' },
            hasUsedTrial: { type: 'boolean', label: 'Has Used Trial' },
        },
        payoutInfo: {
            businessStripeAccountId: { type: 'readonly' },
            useBusinessStripeAccount: { type: 'boolean', label: 'Use business Stripe account for payouts' },
        },
        preferences: {
            serviceCategories: { type: 'readonly', label: 'Service Categories' },
            requirePaymentForBooking: { type: 'boolean', label: 'Require Payment For Booking' },
            allowTips: { type: 'boolean', label: 'Allow Tips At Checkout' },
            minimumBookingNoticeHours: { type: 'number', label: 'Minimum Booking Notice (hours)' },
            enableEmployeeEmails: { type: 'boolean', label: 'Enable Employee Emails' },
            allowAnyAvailableEmployee: { type: 'boolean', label: 'Allow Any Available Employee Option' },
            bookingModel: { type: 'select', label: 'Booking Model', options: ['staff', 'resource'] },
            orchestratorMode: { type: 'boolean', label: 'Enable Orchestrator Mode' },
            primaryOrchestratorId: { type: 'text', label: 'Primary Orchestrator Employee ID' },
        },
    } as const;
    const ORCHESTRATOR_PREFERENCE_KEYS = ['orchestratorMode', 'primaryOrchestratorId'] as const;
    const RESOURCE_MODE_PREFERENCE_KEYS = ['bookingModel', 'allowAnyAvailableEmployee'] as const;
    const PAYMENT_DEPENDENT_PREFERENCE_KEYS = ['requirePaymentForBooking', 'allowTips'] as const;

    const renderDynamicField = (section: keyof BusinessConfig, key: string, value: any) => {
        const spec = (schema as any)[section]?.[key] as FieldSpec | undefined;
        if (!spec) return null;
        const label = spec.label ?? key.charAt(0).toUpperCase() + key.slice(1);
        switch (spec.type) {
            case 'select':
                return (
                    <TextField key={`${section}.${key}`} select label={label} value={value ?? ''}
                        onChange={(e) => handleChange(section, key, e.target.value)} required={!!spec.required} sx={commonSx}>
                        {(spec.options ?? []).map((opt) => {
                            const optionLabel = opt === 'heroOverlay'
                                ? 'Hero Overlay'
                                : opt === 'navbar'
                                    ? 'Navbar'
                                    : opt === 'both'
                                        ? 'Both'
                                        : opt;
                            return (
                                <MenuItem key={opt} value={opt}>
                                    {optionLabel}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                );
            case 'boolean':
                return (
                    <FormControlLabel key={`${section}.${key}`}
                        control={
                            <Checkbox
                                checked={!!value}
                                onChange={(e) => handleChange(section, key, e.target.checked)}
                                disabled={
                                    section === 'payoutInfo' &&
                                    key === 'useBusinessStripeAccount' &&
                                    updatedBusinessConfig?.preferences?.orchestratorMode === true
                                }
                            />
                        }
                        label={label}
                        labelPlacement="start"
                        sx={{ ...commonSx, width: '80%' }} />
                );
            case 'number':
                return (
                    <TextField key={`${section}.${key}`} type="number" label={label} value={value ?? 0}
                        onChange={(e) => handleChange(section, key, Number(e.target.value))} sx={commonSx} />
                );
            case 'textarea':
                return (
                    <TextField key={`${section}.${key}`} label={label} value={value ?? ''}
                        onChange={(e) => handleChange(section, key, e.target.value)} multiline minRows={3} sx={commonSx} />
                );
            case 'readonly':
                return (
                    <TextField key={`${section}.${key}`} label={label} value={String(value ?? '')}
                        sx={commonSx} slotProps={{ input: { readOnly: true } }} disabled />
                );
            case 'url':
            case 'email':
            case 'phone':
            case 'text':
            default:
                return (
                    <TextField key={`${section}.${key}`} label={label} value={value ?? ''}
                        onChange={(e) => handleChange(section, key, e.target.value)} required={!!spec.required} sx={commonSx} />
                );
        }
    };

    const fillWithDemoData = () => {
        const demoConfig: BusinessConfig = {
            businessInfo: {
                name: "Demo Barbershop",
                address: "123 Main Street, Barbertown, USA",
                phone: "1234567890",
                email: "contact@demobarbershop.com",
                websiteDomain: "www.demobarbershop.com",
                timezone: "America/New_York",
                logoImagePath: "",
                businessHours: {
                    monday: '9:00AM - 5:00PM',
                    tuesday: '9:00AM - 5:00PM',
                    wednesday: '9:00AM - 5:00PM',
                    thursday: '9:00AM - 5:00PM',
                    friday: '9:00AM - 5:00PM',
                    saturday: 'CLOSED',
                    sunday: 'CLOSED',
                }
            },
            socials: {
                facebook: "https://facebook.com/demo",
                instagram: "https://instagram.com/demo",
                twitter: "https://twitter.com/demo",
            },
            homepage: {
                heroTitle: "Welcome to Demo Barbershop",
                heroSubtitle: "Your perfect cut starts here",
                googleMapsSrc: "https://www.google.com/maps/embed?pb=demo",
                heroImagePath: "",
                heroLogoImagePath: "",
                gallery: [],
                logoPlacement: 'navbar',
            },
            features: {
                employeeCount: 1,
                loginlessEmployeeCount: 1,
                enableCustomerSMS: false,
                enableEmployeeSMS: false,
                enablePayments: false,
                enableCart: false,
            },
            paymentInfo: {
                owner: "",
                skipSubscriptionCheck: false,
            },
            payoutInfo: {
                useBusinessStripeAccount: false,
                businessStripeAccountId: "",
            },
            preferences: {
                serviceCategories: [],
                requirePaymentForBooking: false,
                allowTips: false,
                minimumBookingNoticeHours: 1,
                enableEmployeeEmails: false,
                allowAnyAvailableEmployee: true,
                bookingModel: 'staff',
                uiFlags: {
                    showHomepageTeamSection: true,
                    showHomepageReviewsSection: true,
                    showHomepageGallerySection: true,
                    showHomepageAboutSection: true,
                    showEmployeeBio: true,
                    showEmployeeSocials: true,
                    showEmployeeProfilePhoto: true,
                    showEmployeePickerInBooking: true,
                },
                terminology: {
                    resourceSingular: 'Employee',
                    resourcePlural: 'Employees',
                    anyAvailableLabel: 'Any Available Staff',
                    selectResourceLabel: 'Select employee',
                },
                orchestratorMode: false,
                primaryOrchestratorId: '',
            },
        };

        setUpdatedBusinessConfig(demoConfig);
        setBusinessHoursEntries(convertHoursToEntries(demoConfig.businessInfo.businessHours));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormLoading(true);
        if (!updatedBusinessConfig || !validateBusinessConfig(updatedBusinessConfig)) {
            setShowErrorDialog(true);
            setFormLoading(false);
            return;
        }
        try {
            updateBusinessConfigMutation.mutate();
        } catch (error) {
            setErrorMessage('Failed to submit the form. Please try again.');
            setShowErrorDialog(true);
        } finally {
            setFormLoading(false);
        }
    };

    const handleRebuildStats = () => {
        setRebuildDialogOpen(true);
    };

    const confirmRebuildStats = () => {
        rebuildStatsMutation.mutate();
    };

    const handleRebuildCustomers = () => {
        setRebuildCustomersDialogOpen(true);
    };

    const confirmRebuildCustomers = () => {
        rebuildCustomersMutation.mutate();
    };

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const validateBusinessConfig = (config: BusinessConfig): boolean => {
        const errors: string[] = [];

        const { name, address, phone, email, timezone, websiteDomain } = config.businessInfo;
        if (!name) errors.push("Business name is required.");
        if (!address) errors.push("Address is required.");
        if (!timezone) errors.push("Timezone is required.");
        if (!/^\d{10}$/.test(phone)) errors.push("Phone number must be 10 digits.");
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push("Invalid email address.");
        const trimmedWebsiteDomain = websiteDomain?.trim();
        if (trimmedWebsiteDomain) {
            const domainPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[A-Za-z]{2,}(\/.*)?$/;
            if (!domainPattern.test(trimmedWebsiteDomain)) {
                errors.push("Website domain must be a valid domain or URL.");
            }
        }

        const socialValidators: Record<string, RegExp> = {
            facebook: /^https:\/\/(www\.)?facebook\.com\//,
            instagram: /^https:\/\/(www\.)?instagram\.com\//,
            twitter: /^https:\/\/(www\.)?(twitter\.com|x\.com)\//,
        };

        const socialErrorMessages: Record<string, string> = {
            facebook: "Facebook URL must start with https://facebook.com/",
            instagram: "Instagram URL must start with https://instagram.com/",
            twitter: "Twitter (X) URL must start with https://x.com/ or https://twitter.com/",
        };

        Object.entries(config.socials).forEach(([key, value]) => {
            const validator = socialValidators[key];
            if (value && !validator.test(value)) {
                errors.push(socialErrorMessages[key]);
            }
        });

        DAYS_OF_WEEK_PREFERRED_ORDER.forEach((day) => {
            const entry = businessHoursEntries[day];
            if (entry.isClosed) return;
            const label = day.charAt(0).toUpperCase() + day.slice(1);
            if (!entry.startTime || !entry.endTime) {
                errors.push(`${label} hours need both start and end times, or mark the day Closed.`);
                return;
            }
            if (!entry.endTime.isAfter(entry.startTime)) {
                errors.push(`${label} hours must have an end time after the start time.`);
            }
        });

        const minimumBookingNoticeHours = config.preferences?.minimumBookingNoticeHours ?? 1;
        if (!Number.isInteger(minimumBookingNoticeHours) || minimumBookingNoticeHours < 0) {
            errors.push("Minimum booking notice must be a non-negative integer number of hours.");
        }

        const terminology = config.preferences?.terminology;
        if (config.preferences?.orchestratorMode === true) {
            if (!terminology?.resourceSingular?.trim()) {
                errors.push("Resource singular label is required.");
            }
            if (!terminology?.resourcePlural?.trim()) {
                errors.push("Resource plural label is required.");
            }
            if (!terminology?.anyAvailableLabel?.trim()) {
                errors.push("Any available label is required.");
            }
            if (!terminology?.selectResourceLabel?.trim()) {
                errors.push("Select resource label is required.");
            }
        }

        const { heroTitle, heroSubtitle, googleMapsSrc, logoPlacement } = config.homepage;
        if (!heroTitle) errors.push("Hero title is required.");
        if (!heroSubtitle) errors.push("Hero subtitle is required.");
        if (!/^https:\/\/www\.google\.com\/maps\/embed\?pb=/.test(googleMapsSrc)) {
            errors.push("Google Maps link must be a valid embed URL.");
        }
        if (!logoPlacement || !['navbar', 'heroOverlay', 'both'].includes(logoPlacement)) {
            errors.push("Logo placement must be set to one of \"navbar\", \"heroOverlay\", or \"both\".");
        }
        if ((logoPlacement === 'heroOverlay' || logoPlacement === 'both') &&
            !config.homepage.heroLogoImagePath &&
            !heroLogoImageChangePlaceHolder) {
            errors.push("A hero overlay logo image is required when the logo placement includes the hero overlay.");
        }
        if ((logoPlacement === 'navbar' || logoPlacement === 'both') &&
            !config.businessInfo.logoImagePath &&
            !logoImageChangePlaceHolder) {
            errors.push("A navbar logo image is required when the logo placement includes the navbar.");
        }

        if (errors.length > 0) {
            setErrorMessage(errors.join("\n"));
            return false;
        }

        return true;
    };

    if (isLoading || !updatedBusinessConfig) {
        return <LoadingSpinner />;
    }
    if (isError) return <Typography color="error" align="center">Failed to load data.</Typography>;

    if (!siteId) {
        return <Typography color="error">Missing site ID</Typography>;
    }

    const onCloseResultDialog = () => {
        setResultDialogOpen(false);
    };
    const paymentsEnabled = updatedBusinessConfig.features?.enablePayments === true;

    return (
        <Container maxWidth="sm">
            <Typography variant="h5" align="center" fontWeight="bold" gutterBottom sx={{ mt: 4, mb: 2 }}>
                Business Configuration
            </Typography>
            <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
                {updatedBusinessConfig &&
                    !updatedBusinessConfig.businessInfo.name &&
                    !updatedBusinessConfig.businessInfo.address &&
                    !updatedBusinessConfig.homepage.heroTitle && (
                        <Button variant="outlined" color="secondary" onClick={fillWithDemoData} sx={{ mb: 2 }}>
                            Fill with Demo Data
                        </Button>
                    )}
            </Box>

            <hr
                style={{
                    border: "none",
                    height: "2px",
                    backgroundColor: "rgba(0,0,0,0.2)",
                    boxShadow: "0 1px 5px rgba(0, 0, 0, 0.3)",
                    marginBottom: "20px",
                }}
            />
            <form onSubmit={handleSubmit}>

                <Typography variant="h6" align="center" gutterBottom sx={{ mt: 4 }}>
                    Business Info
                </Typography>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography variant="overline" align="center" display="block">
                        LOGO IMAGE
                    </Typography>
                    <ImageSelect
                        initialImagePath={businessConfigQuery.data?.businessInfo.logoImagePath}
                        allowDelete={false}
                        styleType="landscape"
                        onImageChange={onLogoImageChange}
                        darkBackground={true}
                        altText="Logo Image"
                    />
                    {/* Business Info Fields (dynamic) */}
                    {Object.keys(schema.businessInfo)
                        .filter((key) => key !== 'logoImagePath' && key !== 'businessHours')
                        .map((key) => renderDynamicField('businessInfo', key as any, (updatedBusinessConfig as any).businessInfo?.[key]))}

                    <Box sx={{ width: '80%', mt: 4 }}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            flexWrap="wrap"
                            gap={1}
                        >
                            <Typography variant="h6">Business Hours</Typography>
                            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setBusinessHoursOpen((prev) => !prev)}
                                >
                                    {businessHoursOpen ? 'Hide Hours' : 'Show Hours'}
                                </Button>
                            </Box>
                        </Box>
                        <Collapse in={businessHoursOpen} timeout="auto" unmountOnExit>
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={1}
                                mb={1}
                            >
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    disabled={matchToggleDisabled}
                                    onClick={handleMatchBusinessHours}
                                    sx={(theme) => ({
                                        textTransform: 'none',
                                        borderColor: theme.palette.secondary.main,
                                        color: theme.palette.secondary.main,
                                        '&:hover': {
                                            borderColor: theme.palette.secondary.main,
                                            backgroundColor: theme.palette.action.hover,
                                        },
                                    })}
                                >
                                    Match Work Hours
                                </Button>
                            </Box>
                            {!ownerWorkHoursEntries && !ownerWorkHoursQuery.isLoading && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                    {ownerId
                                        ? 'No saved work hours were found for this site.'
                                        : 'Assign an owner to enable work hour matching.'}
                                </Typography>
                            )}
                            <Box display="flex" gap={1} flexWrap="wrap" mt={1} mb={2}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleBusinessHoursPreset('default')}
                                >
                                    Set Mon–Fri 9–5
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => handleBusinessHoursPreset('clear')}
                                >
                                    Clear
                                </Button>
                            </Box>
                            {orderedBusinessDays.map((day) => (
                                <BusinessHoursCard
                                    key={day}
                                    day={day}
                                    entry={businessHoursEntries[day]}
                                    onChange={(nextEntry) => handleBusinessHoursEntryChange(day, nextEntry)}
                                />
                            ))}
                        </Collapse>
                    </Box>

                    <Typography variant="h6" align="center" gutterBottom sx={{ mt: 4 }}>
                        Social Media Links
                    </Typography>
                    {/* Social Media Links (dynamic) */}
                    {Object.keys(schema.socials).map((key) => renderDynamicField('socials', key as any, (updatedBusinessConfig as any).socials?.[key]))}

                    {/* Homepage Fields */}
                    <Typography variant="h6" align="center" gutterBottom sx={{ mt: 4 }}>
                        Homepage
                    </Typography>
                    <Box sx={{ width: '80%', mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            Homepage reviews are managed in a separate section.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => navigate(`/admin/superadmin/manage/${siteId}/homepage-reviews`)}
                        >
                            Manage Homepage Reviews
                        </Button>
                    </Box>
                    <Typography variant="overline" align="center" display="block">
                        HERO IMAGE
                    </Typography>
                    <ImageSelect
                        initialImagePath={businessConfigQuery.data ? businessConfigQuery.data!.homepage.heroImagePath : undefined}
                        allowDelete={false}
                        styleType='landscape'
                        onImageChange={onHeroImageChange}
                        altText="Hero Image"
                    />
                    <Typography variant="overline" align="center" display="block" sx={{ mt: 2 }}>
                        HERO OVERLAY LOGO IMAGE
                    </Typography>
                    <ImageSelect
                        initialImagePath={businessConfigQuery.data ? businessConfigQuery.data!.homepage.heroLogoImagePath : undefined}
                        allowDelete={false}
                        styleType='landscape'
                        onImageChange={onHeroLogoImageChange}
                        altText="Hero Logo Image"
                    />

                    {Object.keys(schema.homepage)
                        .filter((key) => key !== 'heroImagePath' && key !== 'gallery')
                        .map((key) => renderDynamicField('homepage', key as any, (updatedBusinessConfig as any).homepage?.[key]))}

                    <Typography variant="h6" align="center" gutterBottom sx={{ mt: 4 }}>
                        Features
                    </Typography>

                    {Object.keys(schema.features).map((key) => renderDynamicField('features', key as any, (updatedBusinessConfig as any).features?.[key]))}

                    {paymentsEnabled && updatedBusinessConfig?.paymentInfo && (
                        <>
                            <Typography variant="h6" align="center" gutterBottom sx={{ mt: 4 }}>
                                Payment Info
                            </Typography>
                            {Object.keys(schema.paymentInfo).map((key) =>
                                renderDynamicField('paymentInfo', key as any, (updatedBusinessConfig as any).paymentInfo?.[key as any])
                            )}
                        </>
                    )}

                    {paymentsEnabled && (
                        <>
                            <Typography variant="h6" align="center" gutterBottom sx={{ mt: 4 }}>
                                Payouts
                            </Typography>

                            {Object.entries(updatedBusinessConfig.payoutInfo ?? {}).map(([key, value]) =>
                                renderDynamicField('payoutInfo', key, value)
                            )}
                        </>
                    )}

                    {updatedBusinessConfig.preferences && (
                        <>
                            <Typography variant="h6" align="center" gutterBottom sx={{ mt: 4 }}>
                                Preferences
                            </Typography>
                            {Object.keys(schema.preferences)
                                .filter((key) =>
                                    !ORCHESTRATOR_PREFERENCE_KEYS.includes(key as typeof ORCHESTRATOR_PREFERENCE_KEYS[number]) &&
                                    !RESOURCE_MODE_PREFERENCE_KEYS.includes(key as typeof RESOURCE_MODE_PREFERENCE_KEYS[number]) &&
                                    (paymentsEnabled || !PAYMENT_DEPENDENT_PREFERENCE_KEYS.includes(key as typeof PAYMENT_DEPENDENT_PREFERENCE_KEYS[number]))
                                )
                                .map((key) =>
                                    renderDynamicField(
                                        'preferences' as keyof BusinessConfig,
                                        key,
                                        (updatedBusinessConfig as any).preferences?.[key]
                                    )
                                )}
                            <Typography variant="subtitle1" align="center" gutterBottom sx={{ mt: 2 }}>
                                Orchestrator
                            </Typography>
                            {ORCHESTRATOR_PREFERENCE_KEYS.map((key) =>
                                renderDynamicField(
                                    'preferences' as keyof BusinessConfig,
                                    key,
                                    (updatedBusinessConfig as any).preferences?.[key]
                                )
                            )}
                            {updatedBusinessConfig.preferences?.orchestratorMode === true &&
                                !updatedBusinessConfig.preferences?.primaryOrchestratorId?.trim() && (
                                    <Typography variant="caption" color="text.secondary" sx={{ width: '80%', mb: 1 }}>
                                        If no admin employee exists yet, we will auto-assign the first created admin employee as
                                        the orchestrator.
                                    </Typography>
                                )}
                            <TextField
                                type="number"
                                label="Loginless Employee Count"
                                value={updatedBusinessConfig.features?.loginlessEmployeeCount ?? 1}
                                onChange={(e) => handleChange('features', 'loginlessEmployeeCount', Number(e.target.value))}
                                disabled={!updatedBusinessConfig.preferences?.orchestratorMode}
                                helperText={
                                    updatedBusinessConfig.preferences?.orchestratorMode
                                        ? 'Max number of loginless profiles allowed in orchestrator mode.'
                                        : 'Enable Orchestrator Mode to edit this limit.'
                                }
                                sx={commonSx}
                            />
                            {updatedBusinessConfig.preferences?.orchestratorMode === true && (
                                <>
                                    <Typography variant="subtitle1" align="center" gutterBottom sx={{ mt: 2 }}>
                                        Resource Mode
                                    </Typography>
                                    {RESOURCE_MODE_PREFERENCE_KEYS.map((key) => (
                                        <React.Fragment key={key}>
                                            {renderDynamicField(
                                                'preferences' as keyof BusinessConfig,
                                                key,
                                                (updatedBusinessConfig as any).preferences?.[key]
                                            )}
                                            {key === 'allowAnyAvailableEmployee' &&
                                                updatedBusinessConfig.preferences?.bookingModel === 'resource' && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ width: '80%', mt: -1, mb: 2, display: 'block' }}
                                                    >
                                                        In resource mode, this behavior is enabled by default.
                                                    </Typography>
                                                )}
                                        </React.Fragment>
                                    ))}
                                </>
                            )}

                            <Typography variant="subtitle1" align="center" gutterBottom sx={{ mt: 2 }}>
                                UI Flags
                            </Typography>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={updatedBusinessConfig.preferences?.uiFlags?.showHomepageTeamSection ?? true}
                                        onChange={(e) => handlePreferenceNestedChange('uiFlags', 'showHomepageTeamSection', e.target.checked)}
                                    />
                                )}
                                label="Show Homepage Team Section"
                                labelPlacement="start"
                                sx={{ ...commonSx, width: '80%' }}
                            />
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={updatedBusinessConfig.preferences?.uiFlags?.showHomepageReviewsSection ?? true}
                                        onChange={(e) => handlePreferenceNestedChange('uiFlags', 'showHomepageReviewsSection', e.target.checked)}
                                    />
                                )}
                                label="Show Homepage Reviews Section"
                                labelPlacement="start"
                                sx={{ ...commonSx, width: '80%' }}
                            />
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={updatedBusinessConfig.preferences?.uiFlags?.showHomepageGallerySection ?? true}
                                        onChange={(e) => handlePreferenceNestedChange('uiFlags', 'showHomepageGallerySection', e.target.checked)}
                                    />
                                )}
                                label="Show Homepage Gallery Section"
                                labelPlacement="start"
                                sx={{ ...commonSx, width: '80%' }}
                            />
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={updatedBusinessConfig.preferences?.uiFlags?.showHomepageAboutSection ?? true}
                                        onChange={(e) => handlePreferenceNestedChange('uiFlags', 'showHomepageAboutSection', e.target.checked)}
                                    />
                                )}
                                label="Show Homepage About Section"
                                labelPlacement="start"
                                sx={{ ...commonSx, width: '80%' }}
                            />
                            {updatedBusinessConfig.preferences?.orchestratorMode === true && (
                                <>
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                checked={updatedBusinessConfig.preferences?.uiFlags?.showEmployeeBio ?? true}
                                                onChange={(e) => handlePreferenceNestedChange('uiFlags', 'showEmployeeBio', e.target.checked)}
                                            />
                                        )}
                                        label="Show Employee Bio"
                                        labelPlacement="start"
                                        sx={{ ...commonSx, width: '80%' }}
                                    />
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                checked={updatedBusinessConfig.preferences?.uiFlags?.showEmployeeSocials ?? true}
                                                onChange={(e) => handlePreferenceNestedChange('uiFlags', 'showEmployeeSocials', e.target.checked)}
                                            />
                                        )}
                                        label="Show Employee Socials"
                                        labelPlacement="start"
                                        sx={{ ...commonSx, width: '80%' }}
                                    />
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                checked={updatedBusinessConfig.preferences?.uiFlags?.showEmployeeProfilePhoto ?? true}
                                                onChange={(e) => handlePreferenceNestedChange('uiFlags', 'showEmployeeProfilePhoto', e.target.checked)}
                                            />
                                        )}
                                        label="Show Employee Profile Photo"
                                        labelPlacement="start"
                                        sx={{ ...commonSx, width: '80%' }}
                                    />
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                checked={updatedBusinessConfig.preferences?.uiFlags?.showEmployeePickerInBooking ?? true}
                                                onChange={(e) => handlePreferenceNestedChange('uiFlags', 'showEmployeePickerInBooking', e.target.checked)}
                                            />
                                        )}
                                        label="Show Employee Picker In Booking"
                                        labelPlacement="start"
                                        sx={{ ...commonSx, width: '80%' }}
                                    />

                                    <Typography variant="subtitle1" align="center" gutterBottom sx={{ mt: 2 }}>
                                        Terminology
                                    </Typography>
                                    <TextField
                                        label="Resource Singular Label"
                                        value={updatedBusinessConfig.preferences?.terminology?.resourceSingular ?? ''}
                                        onChange={(e) => handlePreferenceNestedChange('terminology', 'resourceSingular', e.target.value)}
                                        required
                                        error={!updatedBusinessConfig.preferences?.terminology?.resourceSingular?.trim()}
                                        helperText={!updatedBusinessConfig.preferences?.terminology?.resourceSingular?.trim() ? 'Required' : undefined}
                                        sx={commonSx}
                                    />
                                    <TextField
                                        label="Resource Plural Label"
                                        value={updatedBusinessConfig.preferences?.terminology?.resourcePlural ?? ''}
                                        onChange={(e) => handlePreferenceNestedChange('terminology', 'resourcePlural', e.target.value)}
                                        required
                                        error={!updatedBusinessConfig.preferences?.terminology?.resourcePlural?.trim()}
                                        helperText={!updatedBusinessConfig.preferences?.terminology?.resourcePlural?.trim() ? 'Required' : undefined}
                                        sx={commonSx}
                                    />
                                    <TextField
                                        label="Any Available Label"
                                        value={updatedBusinessConfig.preferences?.terminology?.anyAvailableLabel ?? ''}
                                        onChange={(e) => handlePreferenceNestedChange('terminology', 'anyAvailableLabel', e.target.value)}
                                        required
                                        error={!updatedBusinessConfig.preferences?.terminology?.anyAvailableLabel?.trim()}
                                        helperText={!updatedBusinessConfig.preferences?.terminology?.anyAvailableLabel?.trim() ? 'Required' : undefined}
                                        sx={commonSx}
                                    />
                                    <TextField
                                        label="Select Resource Label"
                                        value={updatedBusinessConfig.preferences?.terminology?.selectResourceLabel ?? ''}
                                        onChange={(e) => handlePreferenceNestedChange('terminology', 'selectResourceLabel', e.target.value)}
                                        required
                                        error={!updatedBusinessConfig.preferences?.terminology?.selectResourceLabel?.trim()}
                                        helperText={!updatedBusinessConfig.preferences?.terminology?.selectResourceLabel?.trim() ? 'Required' : undefined}
                                        sx={commonSx}
                                    />
                                </>
                            )}
                        </>
                    )}

                    <Typography variant="h6" align="center" gutterBottom sx={{ mt: 4 }}>
                        Analytics
                    </Typography>
                    <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
                        <Button
                            variant="outlined"
                            onClick={handleRebuildStats}
                            disabled={rebuildStatsMutation.isPending}
                            sx={{
                                color: 'text.primary',
                                borderColor: 'text.primary',
                                '&:hover': {
                                    borderColor: 'text.primary',
                                    backgroundColor: (theme) => theme.palette.action.hover,
                                },
                                minWidth: 220,
                            }}
                        >
                            {rebuildStatsMutation.isPending ? (
                                <>
                                    <CircularProgress size={18} sx={{ mr: 1 }} /> Rebuilding Analytics Stats...
                                </>
                            ) : (
                                'Rebuild Analytics Stats'
                            )}
                        </Button>
                    </Box>

                    <Typography variant="h6" align="center" gutterBottom sx={{ mt: 2 }}>
                        Customers
                    </Typography>
                    <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
                        <Button
                            variant="outlined"
                            onClick={handleRebuildCustomers}
                            disabled={rebuildCustomersMutation.isPending}
                            sx={{
                                color: 'text.primary',
                                borderColor: 'text.primary',
                                '&:hover': {
                                    borderColor: 'text.primary',
                                    backgroundColor: (theme) => theme.palette.action.hover,
                                },
                                minWidth: 220,
                            }}
                        >
                            {rebuildCustomersMutation.isPending ? (
                                <>
                                    <CircularProgress size={18} sx={{ mr: 1 }} /> Rebuilding Customers...
                                </>
                            ) : (
                                'Rebuild Customers'
                            )}
                        </Button>
                    </Box>

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={formLoading}
                        sx={{ width: '100%', mt: 2, mb: 2 }}
                    >
                        {formLoading ? 'Saving...' : 'Save Configuration'}
                    </Button>
                </Box>
            </form>
            <Dialog
                open={rebuildDialogOpen}
                onClose={() => {
                    if (!rebuildStatsMutation.isPending) {
                        setRebuildDialogOpen(false);
                    }
                }}
            >
                <DialogTitle>Rebuild Analytics Stats</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        This will scan all appointments for this site and rebuild analytics totals. Existing stats will be
                        overwritten where data is found.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRebuildDialogOpen(false)} disabled={rebuildStatsMutation.isPending}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={confirmRebuildStats}
                        disabled={rebuildStatsMutation.isPending}
                        startIcon={rebuildStatsMutation.isPending ? <CircularProgress size={16} /> : undefined}
                    >
                        {rebuildStatsMutation.isPending ? 'Rebuilding...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={rebuildCustomersDialogOpen}
                onClose={() => {
                    if (!rebuildCustomersMutation.isPending) {
                        setRebuildCustomersDialogOpen(false);
                    }
                }}
            >
                <DialogTitle>Rebuild Customers</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        This will scan all appointments for this site and rebuild customer aggregates. Existing customer
                        data will be overwritten where data is found.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRebuildCustomersDialogOpen(false)} disabled={rebuildCustomersMutation.isPending}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={confirmRebuildCustomers}
                        disabled={rebuildCustomersMutation.isPending}
                        startIcon={rebuildCustomersMutation.isPending ? <CircularProgress size={16} /> : undefined}
                    >
                        {rebuildCustomersMutation.isPending ? 'Rebuilding...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Dialog for validation errors */}
            <ErrorDialog
                open={showErrorDialog}
                onClose={() => { setShowErrorDialog(false) }}
                message={errorMessage}
            />
            {/* Dialog for results */}
            <SuccessOrErrorDialog
                open={resultDialogOpen}
                onClose={onCloseResultDialog}
                success={updateBusinessConfigMutation.isSuccess}
                successTitle={'Business Configuration Saved!'}
                successMessage={"Your information has been updated! It should reflect on the website in a few minutes."}
                errorTitle={'Error Occured'}
                errorMessage={'Something went wrong, please try again. ' + errorMessage} />
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default EditBusinessConfigPage;
